import angular from 'angular';
import { IFormModel } from '../form-controller';
import { ThemeServiceType } from '@Shared.Angular/flowingly.services/themeService';
import IFormField from '@Shared.Angular/@types/core/contracts/queryModel/card/formField';
import { FormSectionItemType } from '@Shared.Angular/@types/core/contracts/constants/formSections';
import type {
  IFormSection,
  IFormSectionItem
} from '@Shared.Angular/@types/core/contracts/queryModel/card/formSection';
import IFormattedStepSchema from '@Angular.Runner/interfaces/IFormattedStepSchema';

type IFormFieldsSection = Pick<IFormSection, 'title' | 'description'> & {
  fields: IFormField[];
};

interface IFormFieldsScope extends angular.IScope {
  showDummyDoneButton: boolean;
  form?: IFormModel;
  sections?: IFormFieldsSection[];
  fgTheme?: { primaryButtonColor?: string };
  theme?: { primaryButtonColor?: string };
  primaryButtonContrastColor?: string | null;
}

export const getFormSections = (
  schema?: IFormattedStepSchema
): IFormFieldsSection[] => {
  const fields = schema?.fields ?? [];
  const sections = schema?.sections;

  if (!sections?.length) {
    return [{ fields }];
  }

  const fieldsByName = new Map<string, IFormField>();
  fields.forEach((field) => fieldsByName.set(field.name, field));

  const getFieldsFromItems = (items: IFormSectionItem[]): IFormField[] =>
    items.flatMap((item) => {
      switch (item.type) {
        case FormSectionItemType.FIELD: {
          const field = fieldsByName.get(item.fieldName);
          return field ? [field] : [];
        }
        case FormSectionItemType.COLUMN_LAYOUT:
          return item.columns.flatMap((column) =>
            getFieldsFromItems(column.items)
          );
        default:
          return [];
      }
    });

  const formSections: IFormFieldsSection[] = sections.map(
    (section: IFormSection) => {
      const sectionFields = getFieldsFromItems(section.items ?? []);
      return {
        title: section.title,
        description: section.description,
        fields: sectionFields
      };
    }
  );

  const sectionedFieldNames = new Set(
    sections.flatMap((section) =>
      getFieldsFromItems(section.items ?? []).map((field) => field.name)
    )
  );
  const unsectionedFields = fields.filter(
    (field) => !sectionedFieldNames.has(field.name)
  );

  if (unsectionedFields.length > 0) {
    formSections.push({ fields: unsectionedFields });
  }

  return formSections;
};

angular.module('fg').directive('fgFormFields', [
  'fgConfig',
  'themeService',
  function (fgConfig, themeService: ThemeServiceType) {
    return {
      require: ['^?fgForm'],
      restrict: 'AE',
      templateUrl: 'angular-form-gen/form/form-fields/form-fields.ng.html',
      scope: {},
      link: function ($scope: IFormFieldsScope, $element, $attrs, ctrls) {
        const fgForm = ctrls[0];
        fgForm.model._fields = fgForm.flowFields; // We "merge" these relatively recently added fields to the existing model attribute*/
        fgForm.model.isWorkFlow = fgForm.isWorkflow;

        $scope.showDummyDoneButton = fgConfig.showDummyDoneButton;

        $scope.$watch(
          function () {
            return fgForm.model;
          },
          function (value: IFormModel) {
            $scope.form = value;
          }
        );
        const updateSections = (): void => {
          $scope.sections = getFormSections(fgForm.model.schema);
        };
        $scope.$watch(function () {
          return fgForm.model.schema;
        }, updateSections);
        $scope.$watchCollection(function () {
          return fgForm.model.schema?.fields;
        }, updateSections);
        $scope.$watch(
          function () {
            return ($scope.$parent as IFormFieldsScope).fgTheme;
          },
          function (theme) {
            $scope.theme = theme;

            if (theme?.primaryButtonColor) {
              $scope.primaryButtonContrastColor = themeService.getContrastColor(
                theme.primaryButtonColor
              );
            } else {
              $scope.primaryButtonContrastColor = null;
            }
          }
        );
      }
    };
  }
]);
